import { UIEvent, useCallback, useMemo } from 'react';
import {
  TmdxAvatar,
  TmdxAvatarGroup,
  Listbox,
  ListboxItem,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollShadow,
  SearchInput,
  Selection,
  Tooltip,
} from '@ds';
import clsx from 'clsx';
import { useAsyncSearchableList } from '@/hooks/useAsyncSearchableList';
import { useInfiniteEmployeeListQuery } from '@/queries/employee';
import styles from './ResourceFilter.module.scss';
import { getFullName, getRolesFormatted } from '@/utils/helpers';

type FilterDropdownProps = {
  title: string;
  selectedKeys: string[];
  onSelectedKeysChange: (selectedKeys: string[]) => void;
};

export const ResourceFilter = ({ title, selectedKeys, onSelectedKeysChange }: FilterDropdownProps) => {
  const {
    list: resourceList,
    isLoading,
    count,
    hasNextPage,
    loadNextPage,
    setSearchValue: onResourceSearch,
  } = useAsyncSearchableList({ query: useInfiniteEmployeeListQuery, mapper: (r) => r });

  const selectedKeysSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const onSelection = (keys: Selection) => {
    if (keys === 'all') {
      onSelectedKeysChange(resourceList.map((i) => i.id));
      return;
    }
    onSelectedKeysChange(Array.from(keys as Set<string>));
  };

  const onAvatarSelect = (id: string) => {
    const isResourceSelected = selectedKeysSet.has(id);

    onSelectedKeysChange(isResourceSelected ? selectedKeys.filter((key) => key !== id) : [...selectedKeys, id]);
  };

  const onOpenChange = (open: boolean) => {
    if (!open) {
      onResourceSearch('');
    }
  };

  const hasScrollHandler = !!loadNextPage && hasNextPage;

  const onScroll = useCallback(
    ({ currentTarget }: UIEvent<HTMLDivElement>) => {
      if (!hasScrollHandler || isLoading) return;

      const isScrolledToBottom = currentTarget.scrollHeight - currentTarget.scrollTop <= currentTarget.scrollWidth;

      if (isScrolledToBottom) {
        loadNextPage();
      }
    },
    [hasScrollHandler, loadNextPage, isLoading],
  );

  return (
    <Popover
      placement="bottom-start"
      size="md"
      triggerType="listbox"
      triggerScaleOnOpen={false}
      offset={8}
      onOpenChange={onOpenChange}
      classNames={{
        content: 'min-w-40 bg-base-60 p-2 rounded-md items-stretch',
      }}
    >
      <TmdxAvatarGroup
        max={4}
        total={count}
        size="md"
        color="default"
        isBordered
        renderCount={(resourceCount) => (
          <PopoverTrigger>
            <p className="cursor-pointer text-m-r text-base-10 ml-1">+{resourceCount}</p>
          </PopoverTrigger>
        )}
      >
        {resourceList.map((i) => (
          <Tooltip
            classNames={{
              content: 'px-3 text-nowrap',
            }}
            placement="top"
            key={`group-${i.id}`}
            content={`${i.firstName} ${i.lastName}`}
          >
            <span className="inline-flex">
              <TmdxAvatar
                src={i.profilePhoto}
                name={getFullName(i.firstName, i.lastName)}
                size="md"
                onClick={() => onAvatarSelect(i.id)}
                classNames={{
                  base: clsx('cursor-pointer', { 'ring-base-5': selectedKeysSet.has(i.id) }),
                }}
              />
            </span>
          </Tooltip>
        ))}
      </TmdxAvatarGroup>
      <PopoverContent className={`${styles.resources} max-w-[270px]`}>
        <div className="mb-2">
          <SearchInput placeholder="Search" onChange={(e) => onResourceSearch(e.target.value)} />
        </div>
        <ScrollShadow size={10} className="w-full max-h-56 overflow-auto" onScroll={onScroll}>
          <Listbox
            aria-label={title}
            selectionMode="multiple"
            selectedKeys={selectedKeys}
            hideEmptyContent
            onSelectionChange={onSelection}
            classNames={{
              base: 'p-0',
            }}
            items={resourceList}
          >
            {(item) => (
              <ListboxItem key={item.id} textValue={getFullName(item.firstName, item.lastName)}>
                <div className="flex items-center gap-2">
                  <TmdxAvatar
                    src={item.profilePhoto}
                    size="lg"
                    radius="lg"
                    name={getFullName(item.firstName, item.lastName)}
                  />
                  <div className="flex-1 max-w-[170px]">
                    <div className="text-m-m text-wrap">{getFullName(item.firstName, item.lastName)}</div>
                    <div className="text-s-r text-base-20 truncate">{getRolesFormatted(item.roles)}</div>
                  </div>
                </div>
              </ListboxItem>
            )}
          </Listbox>
        </ScrollShadow>
      </PopoverContent>
    </Popover>
  );
};
